/**
 * Quick-viz helpers ported from cognos-plugin. Each function takes
 * a high-level intent + the inputs that distinguish the call site
 * (sourceRef, inline rows, NL question) and returns a uniform
 * {@link QuickVizOutcome} so the chat-turn lifecycle in ChatPage
 * doesn't have to branch on which helper produced the result.
 *
 * Lifted from `integrations/cognos-plugin/src/panel/viz/quickViz.ts`;
 * paths + auth chain swapped for operate-ui.
 */
import { askVisualize } from "./vizApi";
import type {
  VizRenderPayload, VizStreamEvent, VisualizeRequestBody,
} from "./types";
import { narrateOverData, type NarrateApiError } from "../narrateApi";

export interface QuickVizOutcome {
  /** Chat message text — narrates what happened. */
  text:    string;
  /**
   * Visual to render in the message. {@code null} when no chart was
   * produced (e.g. narrate-only path or a hard failure).
   */
  visual:  {
    vizSpec?:   VizRenderPayload;
    narrative?: string;
    sourceRef?: { kind: string; params: Record<string, unknown> } | null;
  } | null;
  /** True when the pipeline finished with a chart (or, for narrate, a non-empty markdown answer). */
  success: boolean;
  /** Optional debug trace of streaming events. */
  trace:   VizStreamEvent[];
}

export interface QuickVizOptions {
  token?:      string;
  gatewayUrl?: string;
}

/**
 * Run a Cognos report and visualize the result rows. Mirrors
 * `runReportDataViz` in cognos-plugin. The chart bubble's
 * {@code sourceRef} is stamped onto the Visual so follow-up
 * "Re-run with filters" works.
 */
export async function runReportDataViz(
  scope: { path: string; name?: string },
  options: QuickVizOptions = {},
  maxRows = 5_000,
): Promise<QuickVizOutcome> {
  if (!scope?.path) {
    return {
      text:    "Pick a Cognos report first (no scope selected).",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const body: VisualizeRequestBody = {
    intent: `Visualize the data of ${scope.name ?? "this report"}`,
    source: {
      kind:    "ref",
      refKind: "cognos.report.run",
      params:  { searchPath: scope.path, maxRows },
    },
  };
  return await consumeStream(body, options, () =>
    `Report data viz for ${scope.name ?? "this report"} (max ${maxRows} rows)`,
  );
}

/**
 * Run a Cognos 12 dashboard or exploration and visualize the result
 * rows. Routes through the backend's `cognos.idviz.run` ref kind →
 * `SoapCognosReportRunner.runIdVizObject` → `IdVizService` SOAP port.
 *
 * <p><b>Experimental.</b> Dashboards and explorations are JS-based
 * runtimes in Cognos 12, and the IdVizService output contract isn't
 * yet verified end-to-end against real artifacts. First real run
 * may surface a parse error from the backend with a clear "paste
 * this back" hint so the parser can grow.
 */
export async function runIdVizDataViz(
  scope: { path: string; name?: string; objectClass?: string },
  options: QuickVizOptions = {},
  maxRows = 5_000,
): Promise<QuickVizOutcome> {
  if (!scope?.path) {
    return {
      text:    "Pick a Cognos dashboard or exploration first.",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const kind = (scope.objectClass ?? "object").toLowerCase();
  const body: VisualizeRequestBody = {
    intent: `Visualize the data of ${kind} ${scope.name ?? scope.path}`,
    source: {
      kind:    "ref",
      refKind: "cognos.idviz.run",
      params:  { searchPath: scope.path, maxRows },
    },
  };
  return await consumeStream(body, options, () =>
    `${kind} data viz for ${scope.name ?? "this object"} (max ${maxRows} rows, experimental)`,
  );
}

/**
 * Run viz on an uploaded-file content object. Routes through the
 * backend's `cognos.uploadedFile.fetch` ref kind →
 * `SoapCognosReportRunner.runUploadedFile` → ContentManagerService
 * byte fetch + CSV decode. CSV files only on the first cut; Excel /
 * JSON / Parquet land as follow-ups.
 */
export async function runUploadedFileDataViz(
  scope: { path: string; name?: string },
  options: QuickVizOptions = {},
  maxRows = 5_000,
): Promise<QuickVizOutcome> {
  if (!scope?.path) {
    return {
      text:    "Pick a Cognos uploaded file first (no scope selected).",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const body: VisualizeRequestBody = {
    intent: `Visualize the data in ${scope.name ?? "this uploaded file"}`,
    source: {
      kind:    "ref",
      refKind: "cognos.uploadedFile.fetch",
      params:  { searchPath: scope.path, maxRows },
    },
  };
  return await consumeStream(body, options, () =>
    `Uploaded-file viz for ${scope.name ?? "this file"} (max ${maxRows} rows)`,
  );
}

/**
 * Run viz on a Cognos Framework Manager package via a transient
 * one-shot query. Routes through the backend's `cognos.package.run`
 * ref kind → `SoapCognosReportRunner.runPackage` →
 * `ReportService.runSpecification` with a synthesized minimal report
 * spec targeting `<package-path>/model[@name='model']` — the
 * canonical Cognos modelPath shape for FM packages.
 *
 * <p>Unlike data modules (which Cognos's CM resolver chokes on for
 * inline-spec runs), packages resolve cleanly via search-path-form
 * modelPath because `<package>/model[@name='model']` is what
 * Cognos's spec engine was built for.
 */
export async function runPackageDataViz(
  scope: { path: string; name?: string },
  options: QuickVizOptions = {},
  maxRows = 5_000,
  selectedColumns?: string[],
): Promise<QuickVizOutcome> {
  if (!scope?.path) {
    return {
      text:    "Pick a Cognos package first (no scope selected).",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const params: Record<string, unknown> = {
    searchPath: scope.path,
    maxRows,
  };
  if (selectedColumns && selectedColumns.length > 0) {
    params.parameters = { selectedColumns };
  }
  const body: VisualizeRequestBody = {
    intent: `Visualize the data in ${scope.name ?? "this package"}`,
    source: {
      kind:    "ref",
      refKind: "cognos.package.run",
      params,
    },
  };
  return await consumeStream(body, options, () =>
    `Package viz for ${scope.name ?? "this package"} (max ${maxRows} rows)`,
  );
}

/**
 * Run viz on a Cognos 12 data module via a transient one-shot query.
 * Routes through the backend's `cognos.dataModule.run` ref kind →
 * `SoapCognosReportRunner.runDataModule` → ReportService.runSpecification
 * with a synthesized minimal report-spec.
 *
 * <p>The transient spec selects no explicit columns by default —
 * Cognos infers the implicit query subject's columns when supported.
 * If a particular install rejects implicit-selection, the user can
 * call `analysis.describeDataModule` to enumerate columns and pass
 * them through the chat as a follow-up.
 */
export async function runDataModuleDataViz(
  scope: { path: string; name?: string },
  options: QuickVizOptions = {},
  maxRows = 5_000,
): Promise<QuickVizOutcome> {
  if (!scope?.path) {
    return {
      text:    "Pick a Cognos data module first (no scope selected).",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const body: VisualizeRequestBody = {
    intent: `Visualize the data in ${scope.name ?? "this data module"}`,
    source: {
      kind:    "ref",
      refKind: "cognos.dataModule.run",
      params:  { searchPath: scope.path, maxRows },
    },
  };
  return await consumeStream(body, options, () =>
    `Data-module viz for ${scope.name ?? "this module"} (max ${maxRows} rows)`,
  );
}

/**
 * Feature #1: refine a chart against its cached rows. Replays the
 * Specify+Render phases with a new intent against an inline source.
 */
export async function runInlineViz(
  intent: string,
  columnNames: string[],
  rows: Array<Array<unknown>>,
  options: QuickVizOptions = {},
): Promise<QuickVizOutcome> {
  const trimmed = intent.trim();
  if (!trimmed) {
    return { text: "A follow-up intent is required.", visual: null, success: false, trace: [] };
  }
  if (!columnNames || columnNames.length === 0 || !rows) {
    return {
      text:    "Cannot refine — the original chart did not preserve its rows.",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const body: VisualizeRequestBody = {
    intent: trimmed,
    source: {
      kind:    "inline",
      columns: columnNames.map((name) => ({ name })),
      rows,
    },
  };
  return await consumeStream(body, options, () => trimmed);
}

/**
 * Feature #2: re-run a Cognos report with new prompt-parameter values.
 * Merges the new {@code parameters} map into the original sourceRef's
 * params and re-issues the fetch.
 */
export async function runReportRerunViz(
  sourceRef: { kind: string; params: Record<string, unknown> },
  parameters: Record<string, string | string[]>,
  intentLabel: string,
  options: QuickVizOptions = {},
): Promise<QuickVizOutcome> {
  if (sourceRef.kind !== "cognos.report.run") {
    return {
      text:    `Re-run with filters only supports cognos.report.run; got ${sourceRef.kind}.`,
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const searchPath = sourceRef.params["searchPath"];
  if (typeof searchPath !== "string" || searchPath.length === 0) {
    return {
      text:    "Cannot re-run — source chart did not carry a Cognos search path.",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  const mergedParams: Record<string, unknown> = {
    ...sourceRef.params,
    parameters,
  };
  const body: VisualizeRequestBody = {
    intent: intentLabel,
    source: {
      kind:    "ref",
      refKind: "cognos.report.run",
      params:  mergedParams,
    },
  };
  return await consumeStream(body, options, () => intentLabel);
}

/**
 * Feature #3: narrative answer over cached rows. Markdown response
 * lands in the assistant text, no chart produced.
 */
export async function runNarrateAsk(
  intent: string,
  columnNames: string[],
  rows: Array<Array<unknown>>,
): Promise<QuickVizOutcome> {
  const trimmed = intent.trim();
  if (!trimmed) {
    return { text: "A question is required.", visual: null, success: false, trace: [] };
  }
  if (!columnNames || columnNames.length === 0 || !rows) {
    return {
      text:    "Cannot answer — the original chart did not preserve its rows.",
      visual:  null,
      success: false,
      trace:   [],
    };
  }
  try {
    const result = await narrateOverData(trimmed, columnNames, rows);
    return {
      text:    result.markdown || "The model produced no answer.",
      visual:  null,
      success: result.markdown.length > 0,
      trace:   [],
    };
  } catch (e: unknown) {
    const err = e as Partial<NarrateApiError> & { message?: string };
    const detail = err.detail ?? err.message ?? "unknown error";
    return {
      text:    err.code === "llm.gateway.disabled"
        ? `Narrative analysis is unavailable: the multi-provider LLM gateway is off (${detail}).`
        : `Narrative analysis failed: ${detail}`,
      visual:  null,
      success: false,
      trace:   [],
    };
  }
}

/**
 * Shared SSE consumer + outcome shaper. Stamps {@code sourceRef} onto
 * the resulting Visual when the input was a re-runnable ref kind, so
 * downstream affordances (Refine, Filters) compose iteratively.
 */
async function consumeStream(
  body: VisualizeRequestBody,
  options: QuickVizOptions,
  successPreamble: () => string,
): Promise<QuickVizOutcome> {
  const trace: VizStreamEvent[] = [];
  let complete: VizRenderPayload | null = null;
  let firstError: string | null = null;

  try {
    for await (const evt of askVisualize(body, {
      token:      options.token,
      gatewayUrl: options.gatewayUrl,
    })) {
      trace.push(evt);
      if (evt.type === "complete") complete = evt.payload;
      else if (evt.type === "error" && !firstError) {
        firstError = `${evt.payload.phase}: ${evt.payload.message}`;
      }
    }
  } catch (e) {
    firstError = e instanceof Error ? e.message : "unknown viz error";
  }

  if (complete) {
    const choice = trace.find((e) => e.type === "choice");
    const summary = choice?.type === "choice"
        ? `${successPreamble()} — ${choice.payload.rationale}`
        : `${successPreamble()}`;
    // Capture sourceRef for ref kinds whose downstream affordances
    // need to know how the chart was produced:
    //   - "Re-run with filters" — only meaningful for report.run and
    //     idviz.run (those carry prompt parameters).
    //   - "Suggest questions" — needs package.run / dataModule.run so
    //     the backend can fetch BMT schema (tables / columns /
    //     relationships) for the LLM. The component itself filters by
    //     kind, so packaging both classes here is safe.
    const reRunnableRefKinds = new Set([
      "cognos.report.run",
      "cognos.idviz.run",
      "cognos.package.run",
      "cognos.dataModule.run",
    ]);
    // B0 (save-viz Feature B): prefer the backend's effective ref —
    // it carries the enrichment the fetch recorded (resolved item
    // references + model search path) that the request-side ref lacks.
    const sourceRef = complete.sourceRef
        ?? (body.source.kind === "ref" && reRunnableRefKinds.has(body.source.refKind)
            ? { kind: body.source.refKind, params: { ...body.source.params } }
            : null);
    return {
      text:    summary,
      visual:  { vizSpec: complete, narrative: summary, sourceRef },
      success: true,
      trace,
    };
  }

  return {
    text:    `Quick viz failed: ${firstError ?? "no chart was produced"}`,
    visual:  null,
    success: false,
    trace,
  };
}
